'use client';

import { useEffect, useCallback } from 'react';
import { useRouter, usePathname } from 'next/navigation';
import { isAuthenticated, getUserRole } from '@/lib/auth';
import { canRoleAccess, resolveDefaultRedirect } from '@/lib/roleConfig';

const PUBLIC_ROUTES = ['/login', '/verify-otp', '/service', '/'];

function isPublicRoute(route: string | null): boolean {
  if (!route) return false;
  return PUBLIC_ROUTES.some((r) => route === r || route.startsWith(r + '/'));
}

export default function RoleChangeRedirect() {
  const router = useRouter();
  const pathname = usePathname();

  const tryRedirect = useCallback(
    (role: string | null, currentPath: string | null) => {
      if (!isAuthenticated() || isPublicRoute(currentPath) || !currentPath || !role) return;

      if (!canRoleAccess(role, currentPath)) {
        const dest = resolveDefaultRedirect(role);
        if (currentPath !== dest) {
          router.replace(dest);
        }
      }
    },
    [router],
  );

  // Check access whenever pathname changes (covers client-side navigation)
  useEffect(() => {
    tryRedirect(getUserRole(), pathname);
  }, [pathname, tryRedirect]);

  // React to role changes in this tab.
  // This fires only AFTER the auth cookie is updated (see setUserRole in auth.ts),
  // so the middleware will see the new role when router.replace hits the server.
  useEffect(() => {
    const onRoleChanged = () => tryRedirect(getUserRole(), pathname);
    window.addEventListener('userRoleChanged', onRoleChanged);
    return () => window.removeEventListener('userRoleChanged', onRoleChanged);
  }, [pathname, tryRedirect]);

  // Re-check when the dynamic route config loads or changes.
  useEffect(() => {
    const onConfigChanged = () => tryRedirect(getUserRole(), pathname);
    window.addEventListener('routeConfigChanged', onConfigChanged);
    return () => window.removeEventListener('routeConfigChanged', onConfigChanged);
  }, [pathname, tryRedirect]);

  // React to role changes from other browser tabs via localStorage storage event.
  // Small delay to allow the originating tab's cookie fetch to complete first.
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === 'userRole' && e.newValue) {
        setTimeout(() => tryRedirect(e.newValue as string, pathname), 400);
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [pathname, tryRedirect]);

  return null;
}
